import {
  fetchInterviewCard,
  fetchInterviewDrilldown,
  fetchInterviewTableAndChart,
  fetchInterviewVisual,
} from './actions';
import { getDefaultAtsFilters, buildReportsSlice } from '../common/ats-filters';
import { flatTransformer, getTableData } from '../common/flat-transformer';
import { cardTransformer } from '../common/card-transformer';
import { getPercentage } from '../common/get-percentage';

const getChartData = (response) => {
  const {
    chart = [],
    headers,
    lookups: { dt, g1, g2 } = {},
  } = response?.data || {};
  return flatTransformer({ headers, datas: chart, g1, g2, dt });
};

const tableWithTotal = (data) => {
  const interviews_completed_total = data?.reduce(
    (acc, curr) => acc + curr?.interviews_completed,
    0
  );
  const strong_yes_total = data?.reduce(
    (acc, curr) => acc + curr?.strong_yes,
    0
  );
  const strong_no_total = data?.reduce((acc, curr) => acc + curr?.strong_no, 0);
  const yes_total = data?.reduce((acc, curr) => acc + curr?.yes, 0);
  const no_total = data?.reduce((acc, curr) => acc + curr?.no, 0);
  const applicants_interviewed_total = data?.reduce(
    (acc, curr) => acc + curr?.applicants_interviewed,
    0
  );

  function innerFunc(data) {
    if (!data?.length) return [];
    return data.map((datum) => {
      return {
        ...datum,
        interviews_completed_per: getPercentage(
          datum?.interviews_completed,
          interviews_completed_total
        ),
        interviews_completed_total,
        strong_yes_total,
        strong_yes_per: getPercentage(datum?.strong_yes, strong_yes_total),
        strong_no_total,
        strong_no_per: getPercentage(datum?.strong_no, strong_no_total),
        yes_total,
        yes_per: getPercentage(datum?.yes, yes_total),
        no_total,
        no_per: getPercentage(datum?.no, no_total),
        applicants_interviewed_total,
        applicants_interviewed_per: getPercentage(
          datum?.applicants_interviewed,
          applicants_interviewed_total
        ),
        subRows: innerFunc(datum?.subRows),
      };
    });
  }

  return innerFunc(data);
};

const initialState = {
  ...getDefaultAtsFilters(),

  isCardLoading: false,
  cards: {},

  interviewVisuals: [],
  isInterviewVisualsLoading: false,

  chart: [],
  table: [],
  isChartLoading: false,
  isTableLoading: false,
  initialLoading: false,
  isDrillLoading: false,
  drilldownData: [],
  stageMapping: {},
  totalCount: 0,
  currentDrillPage: 1,
};

const constructDrilldownData = (payload) => {
  const k1Map = payload?.data?.lookups?.k1;
  const k2Map = payload?.data?.lookups?.k2;
  const k3Map = payload?.data?.lookups?.k3;
  const k4Map = payload?.data?.lookups?.k4;
  const k5Map = payload?.data?.lookups?.k5;
  const k6Map = payload?.data?.lookups?.k6;
  const k7Map = payload?.data?.lookups?.k7;
  const returnData = payload?.data?.table?.map((data) => {
    // eslint-disable-next-line no-unused-vars
    const [
      dt,
      k1,
      k2,
      k3,
      k4,
      k5,
      k6,
      k7,
      interview_type,
      interview_duration,
      interview_location_type,
      stage_name,
      department_name,
      office_names,
      time_to_fill_scorecard,
    ] = data;
    return {
      interview_time: dt,
      ...k1Map[k1],
      ...k2Map[k2],
      interview: k3Map[k3],
      scorecard_Rating: k4Map[k4],
      job: k5Map[k5],
      scheduledBy: k6Map[k6],
      cancelledBy: k7Map?.[k7],
      interview_type,
      interview_location_type,
      stage_name,
      department_name,
      office_names,
      interview_duration,
      time_to_fill_scorecard,
    };
  });

  return returnData;
};
const extraReducers = {
  [fetchInterviewCard.pending]: (state) => {
    state.isCardLoading = true;
  },
  [fetchInterviewCard.fulfilled]: (state, { payload }) => {
    state.cards = cardTransformer(payload);
    state.isCardLoading = false;
  },
  [fetchInterviewCard.rejected]: (state) => {
    state.isCardLoading = false;
  },

  [fetchInterviewVisual.pending]: (state) => {
    state.isInterviewVisualsLoading = true;
  },
  [fetchInterviewVisual.fulfilled]: (state, { meta, payload }) => {
    state.interviewVisuals = getTableData(payload, meta?.args);
    state.isInterviewVisualsLoading = false;
  },
  [fetchInterviewVisual.rejected]: (state) => {
    state.isInterviewVisualsLoading = false;
  },

  [fetchInterviewTableAndChart.pending]: (state) => {
    state.isChartLoading = true;
    state.isTableLoading = true;
  },

  [fetchInterviewTableAndChart.fulfilled]: (state, { meta, payload = {} }) => {
    state.isChartLoading = false;
    state.isTableLoading = false;
    state.chart = getChartData(payload);
    state.table = tableWithTotal(getTableData(payload, meta?.arg));
  },

  [fetchInterviewTableAndChart.rejected]: (state) => {
    state.isChartLoading = false;
    state.isTableLoading = false;
  },

  [fetchInterviewDrilldown.pending]: (state, { meta }) => {
    const { drilldown } = meta?.arg || {};
    if (drilldown.page === 1) {
      state.initialLoading = true;
    } else {
      state.isDrillLoading = true;
    }
  },
  [fetchInterviewDrilldown.fulfilled]: (state, { meta, payload = {} }) => {
    if (!payload) {
      return;
    }
    state.isDrillLoading = false;
    state.initialLoading = false;

    const { drilldown } = meta?.arg || {};
    const newData = constructDrilldownData(payload);

    state.totalCount = payload?.data?.pagination?.total_count;

    if (drilldown.page === 1) {
      state.drilldownData = newData;
    } else {
      state.drilldownData = [...state.drilldownData, ...newData];
    }
    state.currentDrillPage = drilldown.page;
  },
  [fetchInterviewDrilldown.rejected]: (state, action) => {
    if (
      action.error?.name === 'AbortError' ||
      action.error?.name === 'CanceledError'
    ) {
      return;
    }
    state.isDrillLoading = false;
    state.initialLoading = false;
  },
};

export const { reducer, actions } = buildReportsSlice({
  name: 'atsInterviewReport',
  initialState,
  extraReducers,
});

export const {
  applyFilter,
  resetFilter,
  applyTimeFrame,
  applyViewBy,
  applyGroup,
  setFiltersData,
  applySearch,
  resetDrillDown,
} = actions;
export default reducer;
